import { useMemo, useState } from "react";
import { Search, FileCheck2, ChevronRight, MailCheck, PenLine } from "lucide-react";
import { Input } from "@/components/ui/input";
import { fmtMoney, fmtDate, orNA } from "@/lib/format";
import { useColumnPrefs, ResizeHandle, ColumnPicker } from "@/components/ColumnTools";

const norm = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const day = (v) => (v ? new Date(v).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }) : "");

const badge = (cls, testid, children) => <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${cls}`} data-testid={testid}>{children}</span>;

const COLS = [
  { key: "guest", label: "Huésped", width: 200, render: (c) => <><p className="truncate font-semibold text-slate-900" title={c.guest_name}>{c.guest_name || "Sin nombre"}</p><p className="truncate text-xs text-slate-500" title={c.email}>{orNA(c.email)}</p></> },
  { key: "property", label: "Propiedad", width: 160, className: "text-slate-600", render: (c) => <span className="block truncate" title={c.property_name}>{orNA(c.property_name)}</span> },
  { key: "stay", label: "Estancia", width: 130, className: "text-xs text-slate-600", render: (c) => (c.check_in || c.check_out ? <>{fmtDate(c.check_in)} → {fmtDate(c.check_out)}</> : "Sin fechas") },
  { key: "paid", label: "Pagado", width: 120, className: "text-xs text-slate-500", render: (c) => day(c.paid_at) },
  { key: "amount", label: "Monto", width: 130, className: "whitespace-nowrap font-mono font-semibold text-slate-800", render: (c) => fmtMoney(c.amount_cents, c.currency) },
  { key: "cashier", label: "Cajero", width: 130, className: "text-slate-700", render: (c) => orNA(c.created_by_name) },
  { key: "cert", label: "Certificado", width: 150, render: (c) => <span className="flex flex-wrap gap-1">{c.docs_count > 0 && badge("bg-emerald-50 text-emerald-700", `docs-paid-has-doc-${c.id}`, <><FileCheck2 size={12} />{c.docs_count} doc.</>)}{c.sent_count > 0 ? badge("bg-sky-50 text-sky-700", `docs-paid-sent-${c.id}`, <><MailCheck size={12} />Enviado</>) : badge("bg-amber-50 text-amber-700", `docs-paid-pending-${c.id}`, "Sin certificado")}</span> },
  { key: "sign", label: "Firma", width: 120, render: (c) => (c.signed_count > 0 ? badge("bg-emerald-50 text-emerald-700", `docs-paid-signed-${c.id}`, <><PenLine size={12} />Firmado</>) : c.docs_count > 0 ? badge("bg-rose-50 text-rose-700", `docs-paid-unsigned-${c.id}`, "Sin firma") : <span className="text-xs text-slate-400">—</span>) },
];
const OPEN_W = 90;

export const PaidChargesList = ({ charges, brand, onPick }) => {
  const [q, setQ] = useState("");
  const cols = useColumnPrefs("docs-paid-cols", COLS);
  const tableW = cols.shown.reduce((s, c) => s + cols.widths[c.key], OPEN_W);
  const [pending, setPending] = useState("");
  const unsigned = (c) => c.docs_count > 0 && !c.signed_count;
  const FILTERS = { cert: (c) => !c.sent_count, sign: unsigned };
  const nCert = charges.filter(FILTERS.cert).length;
  const nSign = charges.filter(unsigned).length;
  const shown = useMemo(() => {
    const n = norm(q).trim();
    return charges.filter((c) => (!pending || FILTERS[pending](c)) && (!n || norm(`${c.guest_name} ${c.email} ${c.property_name}`).includes(n)));
  }, [charges, q, pending]); // eslint-disable-line react-hooks/exhaustive-deps
  const chip = (key, label, n, on) => (
    <button type="button" onClick={() => setPending((p) => (p === key ? "" : key))} data-testid={`docs-paid-filter-${key === "cert" ? "pending" : "unsigned"}`} aria-pressed={pending === key}
      className={`rounded-full border px-3 py-2 text-xs font-semibold transition-colors ${pending === key ? on : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
      {label} ({n})
    </button>
  );
  return (
    <div className="rounded-2xl border border-slate-200 bg-white" data-testid="docs-paid-list">
      <div className="flex flex-col gap-3 border-b border-slate-100 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-heading text-lg font-semibold text-slate-900">Ventas pagadas de {brand}</h2>
          <p className="text-xs text-slate-500" data-testid="docs-paid-count">{shown.length} de {charges.length} · elige una para llenar el documento</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
          <div className="flex flex-wrap gap-2">
            <ColumnPicker columns={COLS} prefs={cols} />
            {chip("cert", "Sin certificado", nCert, "border-amber-300 bg-amber-50 text-amber-800")}
            {chip("sign", "Sin firma", nSign, "border-rose-300 bg-rose-50 text-rose-800")}
          </div>
          <div className="relative sm:w-80">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre del huésped" className="pl-9" data-testid="docs-paid-search" />
          </div>
        </div>
      </div>
      <div className="max-h-[60vh] overflow-auto">
        {shown.length > 0 && (
          <table className="table-fixed text-sm" style={{ width: tableW, minWidth: "100%" }} data-testid="docs-paid-table">
            <colgroup>{cols.shown.map((c) => <col key={c.key} style={{ width: cols.widths[c.key] }} />)}<col style={{ width: OPEN_W }} /></colgroup>
            <thead className="sticky top-0 z-20 border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                {cols.shown.map((c) => (
                  <th key={c.key} className="relative truncate bg-slate-50 px-4 py-3 font-semibold" data-testid={`col-header-${c.key}`}>
                    {c.label}<ResizeHandle colKey={c.key} width={cols.widths[c.key]} onResize={cols.resize} />
                  </th>
                ))}
                <th className="right-0 bg-slate-50 px-4 py-3 sm:sticky" />
              </tr>
            </thead>
            <tbody>
              {shown.map((c) => (
                <tr key={c.id} onClick={() => onPick(c.id)} data-testid={`docs-paid-row-${c.id}`} className="group cursor-pointer border-b border-slate-100 transition-colors last:border-0 hover:bg-slate-50">
                  {cols.shown.map((col) => <td key={col.key} className={`overflow-hidden break-words px-4 py-3 ${col.className || ""}`}>{col.render(c)}</td>)}
                  <td className="right-0 bg-white px-4 py-3 text-right shadow-[-8px_0_12px_-10px_rgba(15,23,42,0.25)] transition-colors group-hover:bg-slate-50 sm:sticky">
                    <span className="inline-flex items-center text-xs font-semibold text-[#2F5BFF]" data-testid={`docs-paid-open-${c.id}`}>Abrir<ChevronRight size={14} /></span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {shown.length === 0 && <p className="p-8 text-center text-sm text-slate-500" data-testid="docs-paid-empty">{charges.length ? (pending && !q ? (pending === "sign" ? "Todos los certificados ya están firmados" : "Todas las ventas ya tienen certificado enviado") : "No hay ventas con ese nombre") : `Aún no hay ventas pagadas de ${brand}`}</p>}
      </div>
    </div>
  );
};
